import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';
import { computeMaxVisible } from './overflow';

/** Used when the row's computed gap cannot be read (no layout, e.g. jsdom). */
const FALLBACK_GAP = 2;

/**
 * The gap the row really renders with. It comes from a token
 * (`--nxt-space-2xs`, em-based), so a constant here drifts from the CSS — and
 * every item is charged the difference.
 */
function rowGap(row: HTMLElement): number {
  const gap = Number.parseFloat(getComputedStyle(row).columnGap);
  return Number.isFinite(gap) ? gap : FALLBACK_GAP;
}

export function useNavBarOverflow(
  visibleContainer: Ref<HTMLElement | null>,
  hiddenContainer: Ref<HTMLElement | null>,
  /**
   * Changes whenever the left entries' set or order changes (the entry count
   * alone misses a reorder: other widths then sit at other positions).
   */
  layoutKey: Ref<string | number>
): { maxVisibleLeft: Ref<number> } {
  const maxVisibleLeft = ref<number>(0);

  function recompute() {
    const visible = visibleContainer.value;
    const hidden = hiddenContainer.value;
    if (!visible || !hidden) return;
    // contentRect.width can be fractional; -1 prevents off-by-one trigger flicker.
    const available = visible.clientWidth - 1;
    const widths: number[] = [];
    // Hidden ghost contains itemCount + 1 children: items + the overflow trigger ghost.
    for (let i = 0; i < hidden.childElementCount - 1; i++) {
      widths.push((hidden.children[i] as HTMLElement).offsetWidth);
    }
    const overflowTriggerWidth =
      (hidden.children[hidden.childElementCount - 1] as HTMLElement | undefined)?.offsetWidth ?? 0;
    maxVisibleLeft.value = computeMaxVisible(
      widths,
      overflowTriggerWidth,
      available,
      rowGap(hidden)
    );
  }

  let observer: ResizeObserver | null = null;
  onMounted(() => {
    observer = new ResizeObserver(() => recompute());
    // Both containers must be in the DOM at mount. v-if on either is not
    // supported — the observer attaches once and does not retry.
    if (visibleContainer.value) observer.observe(visibleContainer.value);
    if (hiddenContainer.value) observer.observe(hiddenContainer.value);
    recompute();
  });
  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  // Recompute when entries come, go or swap places (dynamic registration, reorder).
  watch(layoutKey, () => {
    // Allow the hidden DOM to update before we measure.
    queueMicrotask(recompute);
  });

  return { maxVisibleLeft };
}
